import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle } from '../styles/StyledComponents';

const ProficiencyContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProficiencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  width: 100%;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProficiencyCard = styled(motion.div)`
  background-color: var(--white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--primary-blue);
  margin-bottom: 1rem;
  position: relative;
  padding-bottom: 0.75rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: var(--primary-blue);
  }
`;

const CardContent = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CardItem = styled.li`
  margin-bottom: 0.75rem;
  padding-left: 1.5rem;
  position: relative;
  color: var(--dark-blue);
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--primary-blue);
  }
`;

const ProficiencyIntro = styled(motion.div)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 2rem;
  
  p {
    color: var(--medium-gray);
    line-height: 1.8;
    font-size: 1.1rem;
  }
`;

const Proficiency: React.FC = () => {
  const proficiencyData = [
    {
      title: "Medical Coding",
      skills: [
        "ICD-10 Diagnostic Coding",
        "CPT Procedural Coding",
        "HCPCS Level II Coding",
        "Coding Audits & Compliance",
        "Reimbursement Optimization"
      ]
    },
    {
      title: "Claims Processing & Submission",
      skills: [
        "Electronic Claims Submission",
        "Claims Scrubbing & Validation",
        "Denial Management",
        "Appeals Processing",
        "Payment Posting & Reconciliation"
      ]
    },
    {
      title: "Insurance Verification & Prior Authorizations",
      skills: [
        "Eligibility Verification",
        "Benefits Investigation",
        "Prior Authorization Requests",
        "Medical Necessity Documentation",
        "Insurance Follow-up"
      ]
    },
    {
      title: "Healthcare Compliance",
      skills: [
        "HIPAA Compliance",
        "CMS Regulations",
        "FDA/DOH Standards",
        "Documentation Requirements",
        "Audit Preparation & Response"
      ]
    }
  ];

  return (
    <Section id="proficiency" style={{ backgroundColor: 'var(--light-gray)' }}>
      <ProficiencyContainer>
        <SectionTitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Tools & Software Proficiency
        </SectionTitle>
        
        <ProficiencyIntro
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p>
            My expertise spans a wide range of healthcare-specific tools and software, enabling me to streamline workflows, 
            ensure compliance, and optimize revenue cycles across various healthcare settings.
          </p>
        </ProficiencyIntro>
        
        <ProficiencyGrid>
          {proficiencyData.map((item, index) => (
            <ProficiencyCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <CardTitle>{item.title}</CardTitle>
              <CardContent>
                {item.skills.map((skill, i) => (
                  <CardItem key={i}>{skill}</CardItem>
                ))}
              </CardContent>
            </ProficiencyCard>
          ))}
        </ProficiencyGrid>
      </ProficiencyContainer>
    </Section>
  );
};

export default Proficiency;
